import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import FooterAlt from "@/components/FooterAlt";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { blogPosts } from "@/data/blogPosts";
import StructuredData from "@/components/StructuredData";

const Blog = () => {
  useEffect(() => {
    document.title = "Insights Blog: Ecosystem-Led Growth & Hyperscaler Partnerships | AMX";
    const desc = "Expert insights on B2B SaaS growth, hyperscaler partnerships, and venture capital strategy. Learn how to reduce CAC 80%, accelerate sales cycles, and scale through AWS, Azure, GCP ecosystems.";
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", window.location.href);
  }, []);

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "AMX Ventures Blog",
    "description": "Insights on ecosystem-led growth, venture capital, and B2B SaaS strategy",
    "url": "https://amxventures.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "AMX Ventures",
      "logo": "https://amxventures.com/amx-logo-light.svg"
    },
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "datePublished": post.date,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "url": `https://amxventures.com/blog/${post.slug}`
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <StructuredData data={blogSchema} />
      <Navigation />

      <main className="container mx-auto px-4 py-16">
        <header className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
            Insights & Perspectives
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Deep dives into ecosystem-led growth, hyperscaler partnerships, and the future of B2B SaaS from the AMX Ventures team.
          </p>
        </header>

        <section className="max-w-5xl mx-auto">
          <div className="grid gap-8">
            {blogPosts.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="group">
                <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 hover:border-accent">
                  <CardHeader>
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <Badge variant="secondary">{post.category}</Badge>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.readTime}
                        </span>
                      </div>
                    </div>
                    <CardTitle className="text-2xl md:text-3xl group-hover:text-accent transition-colors">
                      {post.title}
                    </CardTitle>
                    <CardDescription className="text-base mt-2">
                      {post.excerpt}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">By {post.author}</span>
                      <span className="flex items-center gap-2 text-accent font-semibold group-hover:gap-3 transition-all">
                        Read Article
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <FooterAlt />
    </div>
  );
};

export default Blog;
